import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, Shield, Camera, Bell, Filter, RefreshCw,
  CheckCircle, Clock, TrendingUp, TrendingDown, Eye, MapPin,
  Calendar, Users, FileText, HardHat, Flame, Activity,
  ChevronRight, XCircle, AlertCircle, BarChart3, PieChart,
  Zap, ArrowUp, ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple mini chart component
const MiniTrendChart = ({ data, color }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data, 1);
  const width = 100;
  const height = 40;
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1 || 1)) * width;
    const y = height - (val / max) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="ml-auto">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
};

const SafetyAlertsDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [stats, setStats] = useState({
    total: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    resolved: 0,
    byType: {},
    trend: []
  });
  const [filter, setFilter] = useState({
    severity: 'all',
    type: 'all',
    status: 'all',
    timeRange: '7d'
  });

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch from multiple sources
      const [cctvRes, incidentsRes, permitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cctv-monitoring/alerts?limit=100`, { headers }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API_URL}/api/incidents?limit=50`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/permits?status=expired,expiring`, { headers }).catch(() => ({ data: { permits: [] } }))
      ]);

      // Transform CCTV alerts
      const cctvAlerts = (cctvRes.data.alerts || []).map(alert => ({
        id: `cctv_${alert.id}`,
        source: 'cctv',
        type: alert.alert_type || 'hazard_detected',
        title: `CCTV: ${(alert.alert_type || 'Hazard').replace(/_/g, ' ')}`,
        description: alert.description || alert.ai_analysis,
        severity: alert.severity || 'medium',
        status: alert.status || 'new',
        location: alert.location,
        camera: alert.feed_name,
        timestamp: alert.timestamp,
        recommended_action: alert.recommended_action
      }));

      // Transform incidents to alerts
      const incidentAlerts = (Array.isArray(incidentsRes.data) ? incidentsRes.data : incidentsRes.data.incidents || []).map(incident => ({
        id: `incident_${incident.id}`,
        source: 'incident',
        type: 'incident',
        title: `Incident: ${incident.title || incident.type}`,
        description: incident.description,
        severity: incident.severity === 'serious' ? 'critical' : incident.severity === 'moderate' ? 'high' : 'medium',
        status: incident.status || 'open',
        location: incident.location,
        timestamp: incident.created_at || incident.date,
        reported_by: incident.reported_by
      }));

      // Transform permits to alerts
      const permitAlerts = (permitsRes.data.permits || []).map(permit => ({
        id: `permit_${permit.id}`,
        source: 'permit',
        type: 'permit_expiring',
        title: `Permit: ${permit.permit_type || permit.type}`,
        description: `${permit.work_description || 'Permit'} - ${permit.status === 'expired' ? 'EXPIRED' : 'Expiring Soon'}`,
        severity: permit.status === 'expired' ? 'critical' : 'high',
        status: permit.status,
        location: permit.location,
        timestamp: permit.expiry_date || permit.end_date
      }));

      // Combine all alerts
      const allAlerts = [...cctvAlerts, ...incidentAlerts, ...permitAlerts]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setAlerts(allAlerts);

      // Calculate stats
      const newStats = {
        total: allAlerts.length,
        critical: allAlerts.filter(a => a.severity === 'critical').length,
        high: allAlerts.filter(a => a.severity === 'high').length,
        medium: allAlerts.filter(a => a.severity === 'medium').length,
        low: allAlerts.filter(a => a.severity === 'low').length,
        resolved: allAlerts.filter(a => a.status === 'resolved' || a.status === 'closed').length,
        byType: {
          cctv: cctvAlerts.length,
          incident: incidentAlerts.length,
          permit: permitAlerts.length
        },
        bySource: {
          ppe_violation: allAlerts.filter(a => a.type === 'ppe_violation').length,
          hazard_detected: allAlerts.filter(a => a.type === 'hazard_detected').length,
          unsafe_behavior: allAlerts.filter(a => a.type === 'unsafe_behavior').length,
          incident: incidentAlerts.length,
          permit: permitAlerts.length
        }
      };
      
      // Calculate 7-day trend
      const now = new Date();
      const trend = [];
      for (let i = 6; i >= 0; i--) {
        const dayStart = new Date(now);
        dayStart.setDate(dayStart.getDate() - i);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);
        
        const dayCount = allAlerts.filter(a => {
          const alertDate = new Date(a.timestamp);
          return alertDate >= dayStart && alertDate <= dayEnd;
        }).length;
        trend.push(dayCount);
      }
      newStats.trend = trend;
      
      setStats(newStats);
      setLastRefresh(new Date());

    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast({ title: "Error", description: "Failed to fetch alerts", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchAlerts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAlerts]);

  // Filter alerts
  const filteredAlerts = alerts.filter(alert => {
    if (filter.severity !== 'all' && alert.severity !== filter.severity) return false;
    if (filter.type !== 'all' && alert.source !== filter.type) return false;
    if (filter.status !== 'all') {
      if (filter.status === 'active' && (alert.status === 'resolved' || alert.status === 'closed')) return false;
      if (filter.status === 'resolved' && alert.status !== 'resolved' && alert.status !== 'closed') return false;
    }
    return true;
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'cctv': return Camera;
      case 'incident': return AlertTriangle;
      case 'permit': return FileText;
      default: return Bell;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'ppe_violation': return HardHat;
      case 'unsafe_behavior': return Users;
      case 'hazard_detected': return AlertTriangle;
      case 'fall_risk': return Activity;
      case 'equipment_issue': return AlertCircle;
      case 'incident': return Flame;
      case 'permit_expiring': return Clock;
      default: return Shield;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Safety Alerts Dashboard</h1>
              <p className="text-slate-500">Consolidated view of all safety alerts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-sm">
              <p className="text-slate-500">Last updated</p>
              <p className="text-slate-700 font-medium">{lastRefresh.toLocaleTimeString()}</p>
            </div>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                autoRefresh 
                  ? 'bg-green-100 text-green-700 border border-green-300' 
                  : 'bg-slate-100 text-slate-600 border border-slate-300'
              }`}
            >
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></span>
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </button>
            <Button onClick={fetchAlerts} variant="outline" className="gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Trend Overview Card */}
        {stats.trend && stats.trend.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-slate-100 to-slate-200 border border-slate-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">7-Day Alert Trend</h3>
                    <p className="text-slate-600 text-sm">
                      {stats.trend.reduce((a, b) => a + b, 0)} total alerts this week
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-900">{stats.trend[stats.trend.length - 1] || 0}</p>
                    <p className="text-slate-600 text-sm">Today</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {stats.trend[stats.trend.length - 1] > stats.trend[stats.trend.length - 2] ? (
                      <ArrowUp className="w-5 h-5 text-red-600" />
                    ) : stats.trend[stats.trend.length - 1] < stats.trend[stats.trend.length - 2] ? (
                      <ArrowDown className="w-5 h-5 text-green-600" />
                    ) : (
                      <span className="w-5 h-5 flex items-center justify-center text-slate-500">—</span>
                    )}
                    <span className={`text-sm ${
                      stats.trend[stats.trend.length - 1] > stats.trend[stats.trend.length - 2] 
                        ? 'text-red-600' 
                        : stats.trend[stats.trend.length - 1] < stats.trend[stats.trend.length - 2]
                          ? 'text-green-600'
                          : 'text-slate-500'
                    }`}>
                      vs yesterday
                    </span>
                  </div>
                  <MiniTrendChart data={stats.trend} color="#3b82f6" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-slate-600 text-sm">Total Alerts</p>
                </div>
                <Bell className="w-8 h-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.critical}</p>
                  <p className="text-red-100 text-sm">Critical</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.high}</p>
                  <p className="text-orange-100 text-sm">High</p>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-400 to-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-yellow-900">{stats.medium}</p>
                  <p className="text-yellow-800 text-sm">Medium</p>
                </div>
                <Activity className="w-8 h-8 text-yellow-700" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.low}</p>
                  <p className="text-green-100 text-sm">Low</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{stats.resolved}</p>
                  <p className="text-blue-100 text-sm">Resolved</p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Source Breakdown with Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/cctv-monitoring'}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Camera className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.byType?.cctv || 0}</p>
                    <p className="text-slate-500 text-sm">CCTV Alerts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/incidents'}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <Flame className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.byType?.incident || 0}</p>
                    <p className="text-slate-500 text-sm">Incidents</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/permits'}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.byType?.permit || 0}</p>
                    <p className="text-slate-500 text-sm">Permit Alerts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions Bar */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button 
            variant="outline" 
            className="gap-2 bg-white"
            onClick={() => window.location.href = '/ai-hazard-scan'}
          >
            <Eye className="w-4 h-4" />
            Scan for Hazards
          </Button>
          <Button 
            variant="outline" 
            className="gap-2 bg-white"
            onClick={() => window.location.href = '/incidents'}
          >
            <AlertTriangle className="w-4 h-4" />
            Report Incident
          </Button>
          <Button 
            variant="outline" 
            className="gap-2 bg-white"
            onClick={() => window.location.href = '/cctv-monitoring'}
          >
            <Camera className="w-4 h-4" />
            Monitor CCTV
          </Button>
          <Button 
            variant="outline" 
            className="gap-2 bg-white"
            onClick={() => window.location.href = '/executive-dashboard'}
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Filters:</span>
              </div>

              <div className="flex gap-2">
                <select
                  value={filter.severity}
                  onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-lg bg-white"
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>

                <select
                  value={filter.type}
                  onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-lg bg-white"
                >
                  <option value="all">All Sources</option>
                  <option value="cctv">CCTV Alerts</option>
                  <option value="incident">Incidents</option>
                  <option value="permit">Permits</option>
                </select>

                <select
                  value={filter.status}
                  onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-lg bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              <div className="ml-auto text-sm text-slate-500">
                Showing {filteredAlerts.length} of {alerts.length} alerts
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <CardTitle>All Safety Alerts</CardTitle>
            <CardDescription>Click on an alert to view details and take action</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredAlerts.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Shield className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No alerts found</p>
                <p className="text-sm">All systems are operating safely</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAlerts.map(alert => {
                  const SourceIcon = getSourceIcon(alert.source);
                  const TypeIcon = getTypeIcon(alert.type);
                  
                  return (
                    <div
                      key={alert.id}
                      className="p-4 border rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group"
                      onClick={() => {
                        if (alert.source === 'cctv') window.location.href = '/cctv-monitoring';
                        else if (alert.source === 'incident') window.location.href = '/incidents';
                        else if (alert.source === 'permit') window.location.href = '/permits';
                      }}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          alert.severity === 'critical' ? 'bg-red-100' :
                          alert.severity === 'high' ? 'bg-orange-100' :
                          alert.severity === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                        }`}>
                          <TypeIcon className={`w-6 h-6 ${
                            alert.severity === 'critical' ? 'text-red-600' :
                            alert.severity === 'high' ? 'text-orange-600' :
                            alert.severity === 'medium' ? 'text-yellow-600' : 'text-green-600'
                          }`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                              {alert.severity?.toUpperCase()}
                            </span>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 flex items-center gap-1">
                              <SourceIcon className="w-3 h-3" />
                              {alert.source?.toUpperCase()}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              alert.status === 'resolved' || alert.status === 'closed' 
                                ? 'bg-green-100 text-green-700' 
                                : alert.status === 'new' 
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-slate-100 text-slate-600'
                            }`}>
                              {alert.status}
                            </span>
                          </div>

                          <h3 className="font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                            {alert.title}
                          </h3>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                            {alert.description}
                          </p>

                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            {alert.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {alert.location}
                              </span>
                            )}
                            {alert.camera && (
                              <span className="flex items-center gap-1">
                                <Camera className="w-3 h-3" />
                                {alert.camera}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(alert.timestamp)}
                            </span>
                          </div>

                          {alert.recommended_action && (
                            <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                              <p className="text-xs text-blue-700">
                                <strong>Action:</strong> {alert.recommended_action}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Arrow */}
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SafetyAlertsDashboard;
